// Edit a draft or confirmed customer order (M7, WP7.2): see orders/form.tsx.

import { Button, EmptyState, PageHeader, toast } from '@enp/ui';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Lock } from 'lucide-react';
import { meQueryOptions } from '../../../../auth/session.js';
import { ApiError } from '../../../../lib/api.js';
import { OrderForm } from '../../../../orders/form.js';
import { OrdersBreadcrumb } from '../../../../orders/parts.js';
import { orderQuery } from '../../../../orders/queries.js';
import { invalidatePlanning } from '../../../../planning/queries.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { accountToday } from '../../../../stock/labels.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/orders/$orderId/edit')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.orderId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(orderQuery(params.orderId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: EditOrderPage,
});

function EditOrderPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const { data: o } = useSuspenseQuery(orderQuery(orderId));
  useDocumentTitle(`Edit ${o.number}`);
  const back = () => void navigate({ to: '/orders/$orderId', params: { orderId } });
  const editable =
    me.permissions.includes('orders.edit') &&
    (o.order_status === 'draft' || o.order_status === 'confirmed');
  return (
    <>
      <PageHeader
        eyebrow={<OrdersBreadcrumb trail={[{ label: o.number, orderId }]} page="Edit" />}
        title={`Edit ${o.number}`}
        description={`${o.customer_name ?? 'The customer'}’s order. Planning checks it again as soon as it’s saved.`}
      />
      {editable ? (
        <OrderForm
          order={o}
          timeZone={me.account.timezone}
          today={accountToday(me.account)}
          branches={[]}
          onCancel={back}
          onSaved={async (saved) => {
            queryClient.setQueryData(orderQuery(orderId).queryKey, saved);
            await invalidatePlanning(queryClient);
            toast.success(`${saved.number} saved`, {
              description: 'The change is in the order’s history. Planning checks it again now.',
            });
            back();
          }}
        />
      ) : (
        <EmptyState
          icon={Lock}
          title={
            me.permissions.includes('orders.edit')
              ? `A ${o.order_status} order can’t be edited`
              : 'Your role doesn’t edit orders'
          }
          description={
            me.permissions.includes('orders.edit')
              ? 'Only draft and confirmed orders change. Take a new order instead.'
              : 'Editing orders needs the “take and edit orders” permission. Ask the owner.'
          }
          action={
            <Button variant="secondary" asChild>
              <Link to="/orders/$orderId" params={{ orderId }}>
                <ArrowLeft aria-hidden />
                Back to the order
              </Link>
            </Button>
          }
        />
      )}
    </>
  );
}
